import {
  AIAVAX_ADDRESS,
  AIAVAX_DECIMALS,
  MONTHLY_TARGET,
  MONTH_COUNT,
  WALLET_ADDRESS,
  sameAddress,
} from "./config";
import type { Erc20Transfer, MonthlySnapshot, MonthlyStat } from "./types";

export function utcMonthStart(date: Date, offset = 0): Date {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + offset, 1),
  );
}

function normalizeTokenValue(value: bigint, decimals: number): bigint {
  if (!Number.isInteger(decimals) || decimals < 0 || decimals > 36) {
    throw new Error(`无效的代币精度：${decimals}`);
  }
  if (decimals === AIAVAX_DECIMALS) return value;
  if (decimals < AIAVAX_DECIMALS) {
    return value * 10n ** BigInt(AIAVAX_DECIMALS - decimals);
  }
  return value / 10n ** BigInt(decimals - AIAVAX_DECIMALS);
}

export function filterIncomingAiAvax(
  transfers: Erc20Transfer[],
): Erc20Transfer[] {
  const seen = new Set<string>();

  return transfers
    .filter(
      (transfer) =>
        sameAddress(transfer.tokenAddress, AIAVAX_ADDRESS) &&
        sameAddress(transfer.to, WALLET_ADDRESS),
    )
    .filter((transfer) => {
      const key = `${transfer.txHash.toLowerCase()}:${transfer.logIndex}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((transfer) => ({
      ...transfer,
      value: normalizeTokenValue(transfer.value, transfer.tokenDecimals),
      tokenDecimals: AIAVAX_DECIMALS,
    }));
}

export function buildMonthlySnapshot(
  transfers: Erc20Transfer[],
  now = new Date(),
  monthCount = MONTH_COUNT,
): MonthlySnapshot {
  if (!Number.isInteger(monthCount) || monthCount <= 0) {
    throw new Error("月份数量必须为正整数");
  }

  const currentStart = utcMonthStart(now);
  const incoming = filterIncomingAiAvax(transfers);
  const months: MonthlyStat[] = [];

  for (let index = 0; index < monthCount; index += 1) {
    const start = utcMonthStart(currentStart, -index);
    const end = utcMonthStart(start, 1);
    const total = incoming.reduce((sum, transfer) => {
      const timestamp = transfer.blockTimestamp * 1_000;
      return timestamp >= start.getTime() && timestamp < end.getTime()
        ? sum + transfer.value
        : sum;
    }, 0n);

    months.push({
      key: `${start.getUTCFullYear()}-${String(start.getUTCMonth() + 1).padStart(2, "0")}`,
      start,
      end,
      incoming: total,
      completed: total >= MONTHLY_TARGET,
      isCurrent: index === 0,
    });
  }

  const currentEnd = utcMonthStart(currentStart, 1);
  const currentTransfers = incoming
    .filter((transfer) => {
      const timestamp = transfer.blockTimestamp * 1_000;
      return (
        timestamp >= currentStart.getTime() &&
        timestamp < currentEnd.getTime()
      );
    })
    .sort((left, right) => right.blockTimestamp - left.blockTimestamp);

  return { capturedAt: now, months, currentTransfers };
}

export function remainingUntilMonthEnd(now = new Date()) {
  const end = utcMonthStart(now, 1);
  const totalHours = Math.max(
    0,
    Math.floor((end.getTime() - now.getTime()) / 3_600_000),
  );
  return {
    days: Math.floor(totalHours / 24),
    hours: totalHours % 24,
    end,
  };
}
